import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import BrandGlyph from "../../components/common/BrandGlyph";
import BrandLogo from "../../components/common/BrandLogo";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store";
import { login, clearError, resendVerification } from "../../features/auth/authSlice";
import "../../styles/auth.scss";
import Swal from "sweetalert2";

// Basit validation şeması
const schema = yup
  .object({
    username: yup.string().required("Username or Email is required").trim(),
    password: yup.string().required("Password is required"),
  })
  .required();

// Form için tip tanımlaması
type LoginFormInputs = {
  username: string;
  password: string;
};

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error } = useAppSelector(
    (state) => state.auth
  );

  // State tanımlamaları
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Ref tanımlamaları
  const cardRef = useRef<HTMLDivElement>(null);

  // React Hook Form - basit konfigürasyon
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Form değerlerini izle
  const watchedValues = watch();

  // Kullanıcı zaten giriş yapmışsa dashboard'a yönlendir
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Redirection logic based on roles and companies
      const user = (window as any).store?.getState().auth.user; // Access store directly or use selector if available
      // Note: We can't access store directly easily here without importing it, but we have useAppSelector.
      // However, user might not be fully loaded yet if we just set isAuthenticated.
      // Let's rely on the user from selector.
    }
  }, [isAuthenticated, isLoading, navigate]);

  // We need the user object to decide where to redirect
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Wait for full user data (id should not be -1)
      if (user.id === -1) return;

      // Check roles
      const isAgencyOwner = user.roles.includes("agency_owner");
      const isCompanyOwner = user.roles.includes("company_owner");
      const isAdmin = user.roles.includes("admin") || user.roles.includes("superadmin");
      const isSpecialist = user.roles.includes("marketing_specialist") || user.roles.includes("company_specialist");

      if (isAdmin || isSpecialist) {
        navigate("/dashboard", { replace: true });
      } else if (isAgencyOwner || isCompanyOwner) {
        if (user.companies && user.companies.length > 0) {
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/onboarding", { replace: true });
        }
      } else {
        // Fallback
        navigate("/dashboard", { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Component unmount olduğunda temizlik
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Form gönderildiğinde
  const onSubmit = useCallback(
    async (data: LoginFormInputs) => {
      // Remember me durumunu localStorage'a kaydet
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("lastUsername", data.username);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("lastUsername");
      }

      const resultAction = await dispatch(login(data));

      if (login.rejected.match(resultAction)) {
        // Hata durumunda yapılacak işlemler (gerekirse)
      }
    },
    [dispatch, rememberMe, navigate]
  );

  // Şifreyi göster/gizle
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  // Remember me toggle
  const handleRememberMeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRememberMe(e.target.checked);
    },
    []
  );

  // Input focus handlers
  const handleInputFocus = useCallback((fieldName: string) => {
    setFocusedField(fieldName);
  }, []);

  const handleInputBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  // Placeholder'ın görünüp görünmeyeceğini kontrol et
  const shouldShowPlaceholder = useCallback(
    (fieldName: keyof LoginFormInputs, placeholder: string) => {
      const fieldValue = watchedValues[fieldName];
      const isFocused = focusedField === fieldName;

      // Eğer alan odaklanmışsa veya değer varsa placeholder'ı gizle
      if (isFocused || (fieldValue && fieldValue.length > 0)) {
        return "";
      }
      return placeholder;
    },
    [watchedValues, focusedField]
  );

  // Register fonksiyonlarını özelleştir
  const usernameRegister = register("username");
  const passwordRegister = register("password");

  // Custom event handlers
  const handleUsernameFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputFocus("username");
      usernameRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputFocus, usernameRegister]
  );

  const handleUsernameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputBlur();
      usernameRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputBlur, usernameRegister]
  );

  const handlePasswordFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputFocus("password");
      passwordRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputFocus, passwordRegister]
  );

  const handlePasswordBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      handleInputBlur();
      passwordRegister.onBlur(e); // React Hook Form'un kendi handler'ını çağır
    },
    [handleInputBlur, passwordRegister]
  );

  // Remember me durumunu localStorage'dan oku
  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    const savedUsername = localStorage.getItem("lastUsername");

    if (savedRememberMe && savedUsername) {
      setRememberMe(true);
      reset({ username: savedUsername, password: "" });
    }
  }, [reset]);

  return (
    <div className="auth-page login-page">
      <div className="gradient-orb"></div>
      <div className="auth-container">
        <div className="auth-card-wrapper">
          <div ref={cardRef} className="auth-card">
            {/* Brand Header */}
            <div className="auth-logo">
              <BrandLogo height={28} />
            </div>

            <p className="form-subtitle">Login to your Meylo account</p>

            {/* Hata mesajı */}
            {error && (
              <div className="auth-error" role="alert">
                {error}
                {error.toLowerCase().includes("not verified") && (
                  <div className="verification-action-container">
                    <p className="verification-text">
                      Your email address has not been verified yet. Please verify your email to continue.
                    </p>
                    <button
                      type="button"
                      className="verification-button"
                      onClick={async () => {
                        // Ask for email address
                        const { value: email } = await Swal.fire({
                          title: 'Verify Email',
                          input: 'email',
                          inputLabel: 'Please enter your email address',
                          inputValue: watchedValues.username || '',
                          showCancelButton: true,
                          confirmButtonText: 'Send Verification Code',
                          showLoaderOnConfirm: true,
                          customClass: {
                            popup: 'swal-modern-popup',
                            title: 'swal-modern-title',
                            input: 'swal-modern-input',
                            confirmButton: 'swal-modern-confirm',
                            cancelButton: 'swal-modern-cancel'
                          },
                          preConfirm: async (email) => {
                            try {
                              await dispatch(resendVerification(email)).unwrap();
                              return email;
                            } catch (error) {
                              Swal.showValidationMessage(
                                `Request failed: ${error}`
                              );
                            }
                          },
                          allowOutsideClick: () => !Swal.isLoading()
                        });

                        if (email) {
                          // Verification sayfasına yönlendir
                          localStorage.setItem("pending_verification_email", email);
                          navigate("/verify-email", { state: { email } });
                        }
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      Verify Email Now
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Login formu */}
            <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="auth-input-group">
                <label className="auth-label" htmlFor="username">
                  Username or Email
                </label>
                <div className="input-container">
                  <input
                    type="text"
                    className={`auth-input ${errors.username ? "is-invalid" : ""}`}
                    id="username"
                    placeholder={shouldShowPlaceholder(
                      "username",
                      "Enter your username or email"
                    )}
                    autoComplete="username"
                    {...usernameRegister}
                    onFocus={handleUsernameFocus}
                    onBlur={handleUsernameBlur}
                  />
                </div>
                {errors.username && (
                  <div className="error-message">{errors.username.message}</div>
                )}
              </div>

              <div className="auth-input-group">
                <label className="auth-label" htmlFor="password">
                  Password
                </label>
                <div className="input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`auth-input ${errors.password ? "is-invalid" : ""}`}
                    id="password"
                    placeholder={shouldShowPlaceholder(
                      "password",
                      "Enter your password"
                    )}
                    autoComplete="current-password"
                    {...passwordRegister}
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <div className="error-message">{errors.password.message}</div>
                )}
              </div>

              <div className="auth-options">
                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="custom-checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                  />
                  <label htmlFor="rememberMe">Remember me</label>
                </div>
                <Link to="/forgot-password" className="forgot-link">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className={`auth-button ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Logging in...</span>
                  </>
                ) : (
                  "Login"
                )}
              </button>

              {/* Self-service registration is disabled; users are created by admins.
              <div className="register-prompt">
                <p>
                  Don't have an account?{" "}
                  <Link to="/register" className="register-link" style={{ fontWeight: "bold" }}>
                    Register
                  </Link>
                </p>
              </div>
              */}
              {/* Implement: if you are new for meylo, you can learn more infro meylo.co.uk create this section. */}
              <div className="register-prompt">
                <p style={{ textAlign: "center", marginTop: "1rem" }}>
                  New to Meylo?{" "}
                  <Link
                    to="https://meylo.co.uk"
                    target="_blank"
                    className="register-link"
                    style={{ fontWeight: "bold" }}
                  >
                    Discover Meylo
                  </Link>
                </p>
              </div>
            </form>
          </div>

          <div className="copyright">
            <div className="copyright-content">
              <span>&copy; {new Date().getFullYear()} Meylo</span>
              <span className="separator">|</span>
              <span>Developed by</span>
              <a
                href="https://ai-websolutions.com"
                target="_blank"
                rel="noopener noreferrer"
                className="developed-by-link"
              >
                <img
                  src="https://www.ai-websolutions.com/assets/favicon.png"
                  alt="AI Web Solutions"
                  className="developed-by-logo"
                />
                <span>AI Web Solutions</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
